"use client";

// eslint-disable-next-line @faire/enforce-internal-folder-encapsulation
import { productClickForSearchPageParametersILeanProductTileAdapter } from "@faire/retailer-visitor-shared/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/productTileActions/useOnProductClickForSearchPage";
import { DiscoveryProductTile } from "@faire/retailer-visitor-shared/features/MarketplaceItems/ProductDetailsTile/index";
import { IProductImageProps } from "@faire/retailer-visitor-shared/features/MarketplaceItems/ProductImage";
import { usePrefetchOnTouch } from "@faire/retailer-visitor-shared/hooks/pdpPrefetching/usePDPPrefetchOnTouch";
import { useProductTileClickImpressions } from "@faire/retailer-visitor-shared/hooks/ProductTileImpression/useProductTileClickImpressions";
import { useIsLoggedInRetailer } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import {
  SignUpElement,
  SignUpType,
  useTrackSignupModalOpenClick,
} from "@faire/retailer-visitor-shared/lib/popSignUp";
import { useSettingPrefetchPdpApis } from "@faire/retailer-visitor-shared/settings/useSettingFEPrefetchPdpApis";
import { useSettingPersistQuickAddOnSearchTile } from "@faire/retailer-visitor-shared/settings/useSettingPersistQuickAddOnSearchTile";
import { usePreloadNavigationToPDP } from "@faire/retailer-visitor-shared/views/product/preload/preloadNavigationToPDP";
import { PRODUCT_GRID_IMAGE_BREAKPOINTS } from "@faire/retailer-visitor-shared/views/search/const";
import { getLocation } from "@faire/web--source/common/globals/getLocation";
import { useEffectEvent } from "@faire/web--source/ui/hooks/useEffectEvent";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { IBadge } from "@faire/web-api--source/indigofair/badges/IBadge";
import { ILeanProductTile } from "@faire/web-api--source/indigofair/data/ILeanProductTile";
import { IProduct } from "@faire/web-api--source/indigofair/data/IProduct";
import { Location } from "history";
import * as React from "react";
import { useRef } from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";

export namespace MarketplaceProduct {
  // Everything is optional. Component with empty props is used as placeholder.
  export interface IProps {
    recordAdImpression?: boolean;
    children?: React.ReactNode | undefined;
    className?: string;
    role?: string;
    productTile?: ILeanProductTile;
    brand?: ILeanProductTile.IBrand;

    title?: string | undefined;

    // onClick is just for side-effect purposes, this component will navigate to
    // the url provided by generateProductLinkProps
    onClick?: (
      product: ILeanProductTile.ILeanProduct,
      event: React.MouseEvent<HTMLElement>
    ) => void;
    generateProductLinkProps?: (
      productToken: string,
      location: Location | undefined
    ) =>
      | {
          productUrl: string;
          replace?: boolean;
        }
      | undefined;

    onQuickAdd?: (newQuantity?: number) => void;
    onQuickAddSubmit?: (newQuantity?: number) => void;

    onChooseOption?: () => void;
    onHeartClicked?: () => void;
    onSaveToBoard?: (boardToken?: string) => void;
    onRemoveFromBoard?: (boardToken?: string) => void;
    onChooseOptionQuantityUpdate?: (newQuantity?: number) => void;

    // onBrandClick is just for side-effect purposes, this component will
    // navigate to the url provided by generateBrandUrl
    onBrandClick?: (
      productToken: string,
      brandToken: string,
      isPromoted?: boolean,
      promotedBy?: Array<keyof typeof IProduct.PromotedType>,
      impressionId?: string,
      clickEvent?: React.MouseEvent<HTMLElement>
    ) => void;
    generateBrandUrl?: (brandToken: string) => string | undefined;
    showVerticalImagery?: boolean;
    onProductVisible?: (
      productToken?: string,
      topLeftTileBadge?: IBadge
    ) => void;
    shouldShowPromotedBadge?: boolean;
    altTag?: string;
    imageDimension?: IProductImageProps["imageDimension"];
    shouldHideSaveToBoard?: boolean;
    imageBreakpoints?: string;
  }
}

const MarketplaceProductLoggedIn: React.FC<MarketplaceProduct.IProps> = (
  props
) => {
  const {
    recordAdImpression,
    productTile,
    brand,
    onQuickAdd,
    onQuickAddSubmit,
    onChooseOption,
    onHeartClicked,
    onSaveToBoard,
    onRemoveFromBoard,
    onChooseOptionQuantityUpdate,
    generateBrandUrl,
    generateProductLinkProps,
    showVerticalImagery,
    onClick,
    onProductVisible,
    shouldShowPromotedBadge,
    altTag,
    imageDimension,
    shouldHideSaveToBoard,
    imageBreakpoints,
  } = props;

  const {
    recordProductBadgeViewEvent,
    recordBrandNameClickEvent,
    recordProductTileClickEvent,
    recordProductTileMiddleClickEvent,
    recordHeartClickedEvent,
    recordProductTileRightClickEvent,
    recordSaveToBoardSubmitEvent,
    recordRemoveFromBoardSubmitEvent,
    recordQuickAddButtonClickEvent,
    recordQuickAddSubmitEvent,
    recordChooseOptionClickEvent,
    recordChooseOptionQuantityUpdateSubmitEvent,
    recordProductTileTouchStartEvent,
  } = useProductTileClickImpressions(
    productClickForSearchPageParametersILeanProductTileAdapter(productTile),
    brand?.token,
    recordAdImpression
  );

  const viewport = useViewport();

  const location = getLocation();

  const topLeftTileBadge = useRef<IBadge | undefined>(undefined);

  const preloadNavigationToPDP = usePreloadNavigationToPDP();
  const prefetchPdpApis = useSettingPrefetchPdpApis();

  const onTopLeftTileBadgeVisible = (tileBadge?: IBadge) => {
    recordProductBadgeViewEvent();
    topLeftTileBadge.current = tileBadge;
    onProductVisible?.(productTile?.product?.token, tileBadge);
  };

  const productLinkProps =
    productTile?.product?.token && location
      ? generateProductLinkProps?.(productTile.product.token, {
          ...location,
          state: {},
        })
      : undefined;

  const { onTouchStart } = usePrefetchOnTouch(
    {
      productToken: productTile?.product?.token,
      bestImage: productTile?.best_image,
      images: productTile?.images ?? [],
      brandToken: productTile?.product?.brand_token,
    },
    productLinkProps?.productUrl ?? ""
  );

  const prefetchPDPDataOnMouseDown = (
    productTile: ILeanProductTile | undefined
  ) => {
    if (!prefetchPdpApis || !productTile) {
      return;
    }
    preloadNavigationToPDP(
      {
        productToken: productTile.product?.token,
        brandToken: productTile.product?.brand_token,
        bestImage: productTile.best_image,
        images: productTile.images,
        showVerticalImagery: props.showVerticalImagery,
      },
      productLinkProps?.productUrl ?? ""
    );
  };

  const onProductClick = useEffectEvent(
    (productTile: ILeanProductTile, event: React.MouseEvent<HTMLElement>) => {
      preloadNavigationToPDP(
        {
          productToken: productTile.product?.token,
          brandToken: productTile.product?.brand_token,
          bestImage: productTile.best_image,
          images: productTile.images,
          showVerticalImagery,
        },
        productLinkProps?.productUrl ?? ""
      );
      recordProductTileClickEvent();
      if (productTile.product) {
        onClick?.(productTile.product, event);
      }
    }
  );

  const onBrandClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    recordBrandNameClickEvent();
    if (productTile?.product && props.onBrandClick) {
      props.onBrandClick(
        productTile.product.token!,
        productTile.product.brand_token!,
        productTile.product.is_promoted,
        productTile.product.promoted_by,
        productTile.product.impression_id,
        event
      );
    }

    event.stopPropagation();
  };

  const handleOnAuxClick = useEffectEvent(
    (event: React.MouseEvent<HTMLElement>) => {
      if (event.button === 1) {
        recordProductTileMiddleClickEvent();
      } else if (event.button === 2) {
        recordProductTileRightClickEvent();
      }
    }
  );
  const handleOnTouchStart = () => {
    recordProductTileTouchStartEvent();
  };

  const handleOnHeartClicked = () => {
    recordHeartClickedEvent();
    onHeartClicked?.();
  };
  const handleOnSaveToBoard = (boardToken?: string) => {
    recordSaveToBoardSubmitEvent(boardToken);
    onSaveToBoard?.(boardToken);
  };
  const handleOnRemoveFromBoard = (boardToken?: string) => {
    recordRemoveFromBoardSubmitEvent(boardToken);
    onRemoveFromBoard?.(boardToken);
  };

  const handleQuickAdd = (newQuantity?: number) => {
    recordQuickAddButtonClickEvent();
    onQuickAdd?.(newQuantity);
  };
  const handleQuickAddSubmit = (newQuantity?: number) => {
    recordQuickAddSubmitEvent(newQuantity);
    onQuickAddSubmit?.(newQuantity);
  };
  const handleChooseOption = () => {
    recordChooseOptionClickEvent();
    onChooseOption?.();
  };
  const handleChooseOptionQuantityUpdate = (newQuantity?: number) => {
    recordChooseOptionQuantityUpdateSubmitEvent(newQuantity);
    onChooseOptionQuantityUpdate?.(newQuantity);
  };

  const persistQuickAddSettingValue = useSettingPersistQuickAddOnSearchTile();
  const hideAddToCartAndNotifyMe =
    !persistQuickAddSettingValue && viewport.isMobile;
  return (
    <DiscoveryProductTile
      productTile={productTile}
      showVerticalImagery={props.showVerticalImagery}
      brand={brand}
      onBrandLinkClick={onBrandClick}
      generateBrandUrl={generateBrandUrl}
      onProductLinkClick={onProductClick}
      prefetchPDPOnMouseDown={() => prefetchPDPDataOnMouseDown(productTile)}
      onAuxClick={handleOnAuxClick}
      onTopLeftBadgeVisible={onTopLeftTileBadgeVisible}
      altText={altTag}
      productLinkProps={productLinkProps}
      shouldShowPromotedBadge={shouldShowPromotedBadge}
      onHeartClicked={handleOnHeartClicked}
      onSaveToBoard={handleOnSaveToBoard}
      onRemoveFromBoard={handleOnRemoveFromBoard}
      onQuickAdd={handleQuickAdd}
      onQuickAddSubmit={handleQuickAddSubmit}
      onChooseOption={handleChooseOption}
      onChooseOptionQuantityUpdate={handleChooseOptionQuantityUpdate}
      hideAddToCartAndNotifyMe={hideAddToCartAndNotifyMe}
      recordAdImpression={recordAdImpression}
      imageDimension={imageDimension}
      shouldHideSaveToBoard={shouldHideSaveToBoard}
      imageBreakpoints={imageBreakpoints}
      onTouchStart={handleOnTouchStart}
      onTouchStartPrefetch={onTouchStart}
    />
  );
};

type IMarketplaceProductLoggedOutProps = Pick<
  MarketplaceProduct.IProps,
  | "recordAdImpression"
  | "productTile"
  | "brand"
  | "children"
  | "generateBrandUrl"
  | "generateProductLinkProps"
  | "showVerticalImagery"
  | "onClick"
  | "onBrandClick"
  | "onProductVisible"
  | "altTag"
  | "imageBreakpoints"
>;

const MarketplaceProductLoggedOut = ({
  recordAdImpression,
  productTile,
  brand,
  generateBrandUrl,
  generateProductLinkProps,
  showVerticalImagery,
  onClick,
  onProductVisible,
  altTag,
  imageBreakpoints,
  onBrandClick,
}: IMarketplaceProductLoggedOutProps) => {
  const {
    recordProductBadgeViewEvent,
    recordProductTileClickEvent,
    recordProductTileMiddleClickEvent,
    recordProductTileRightClickEvent,
    recordBrandNameClickEvent,
    recordProductTileTouchStartEvent,
  } = useProductTileClickImpressions(
    productClickForSearchPageParametersILeanProductTileAdapter(productTile),
    brand?.token,
    recordAdImpression
  );
  const location = useLocation();
  const recordSignUpModalOpenClickEvent = useTrackSignupModalOpenClick();
  const topLeftTileBadge = useRef<IBadge | undefined>(undefined);
  const onTopLeftTileBadgeVisible = (tileBadge?: IBadge) => {
    recordProductBadgeViewEvent();
    topLeftTileBadge.current = tileBadge;
    onProductVisible?.(productTile?.product?.token, tileBadge);
  };

  const preloadNavigationToPDP = usePreloadNavigationToPDP();

  const handleOnProductClick = useEffectEvent(
    (productTile: ILeanProductTile, event: React.MouseEvent<HTMLElement>) => {
      recordProductTileClickEvent();
      preloadNavigationToPDP(
        {
          productToken: productTile.product?.token,
          brandToken: productTile.product?.brand_token,
          bestImage: productTile.best_image,
          images: productTile.images,
          showVerticalImagery,
        },
        productLinkProps?.productUrl ?? ""
      );
      if (productTile.product) {
        onClick?.(productTile.product, event);
      }
      recordSignUpModalOpenClickEvent(
        SignUpType.PRODUCT,
        SignUpElement.PRODUCT_GRID,
        `logged_out_product_tile`
      );
    }
  );

  const productLinkProps =
    productTile?.product?.token && location
      ? generateProductLinkProps?.(productTile.product.token, location)
      : undefined;

  const onBrandLinkClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    recordBrandNameClickEvent();
    if (productTile?.product && onBrandClick) {
      onBrandClick(
        productTile.product.token!,
        productTile.product.brand_token!,
        productTile.product.is_promoted,
        productTile.product.promoted_by,
        productTile.product.impression_id,
        event
      );
    }

    event.stopPropagation();
  };

  const handleOnAuxClick = useEffectEvent(
    (event: React.MouseEvent<HTMLElement>) => {
      if (event.button === 1) {
        recordProductTileMiddleClickEvent();
      } else if (event.button === 2) {
        recordProductTileRightClickEvent();
      }
    }
  );

  const handleOnTouchStart = () => {
    recordProductTileTouchStartEvent();
  };

  return (
    <DiscoveryProductTile
      productTile={productTile}
      onBrandLinkClick={onBrandLinkClick}
      showVerticalImagery={showVerticalImagery}
      brand={brand}
      generateBrandUrl={generateBrandUrl}
      onProductLinkClick={handleOnProductClick}
      onAuxClick={handleOnAuxClick}
      onTouchStart={handleOnTouchStart}
      onTopLeftBadgeVisible={onTopLeftTileBadgeVisible}
      altText={altTag}
      productLinkProps={productLinkProps}
      shouldShowPromotedBadge={false}
      recordAdImpression={recordAdImpression}
      imageBreakpoints={imageBreakpoints}
    />
  );
};

const MarketplaceProductBase: React.FC<MarketplaceProduct.IProps> = (props) => {
  const isLoggedInRetailer = useIsLoggedInRetailer();
  if (isLoggedInRetailer) {
    return (
      <MarketplaceProductLoggedIn
        {...props}
        imageBreakpoints={PRODUCT_GRID_IMAGE_BREAKPOINTS}
      />
    );
  }

  return (
    <MarketplaceProductLoggedOut
      {...props}
      imageBreakpoints={PRODUCT_GRID_IMAGE_BREAKPOINTS}
    />
  );
};

export const MarketplaceProduct = styled(MarketplaceProductBase)``;
